import { Dispatch, SetStateAction, useEffect } from 'react'
import { UseFormClearErrors, UseFormSetError, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { IBAN_VALIDATION_ERRORS } from 'pages/ManageBeneficiaries/constants'
import {
  BaseErrorResponse,
  BeneficiaryDetails,
  BeneficiaryDetailsCurrency,
  PaymentMethod,
  ValidateIbanResponse,
  ValidateRoutingNumberResponse,
  ValidateScanResponse,
} from 'types/beneficiaries'
import { MappedErrorMessageState } from 'types/errors'
import { ContactFiatInstrumentFormData } from '../schemas/contactFiatInstrumentSchema'
import { FieldsFetchedFromIbanValidation } from '../types/fiatInstruments'
import {
  getIsValidIban,
  resetBankAddress,
  setBankAccountFieldsFetchedFromIbanValidation,
} from '../utils/fiatInstruments'

type IbanValidationProps = {
  ibanBankDetails: ValidateIbanResponse
  isValidatingIbanError: boolean
  setIsValidIban: Dispatch<SetStateAction<boolean>>
  setIbanBankDetailsData: (details: FieldsFetchedFromIbanValidation) => void
}

type BicValidationProps = {
  bicValidationData: string
  isValidatingBicError: boolean
  bicValidationError?: BaseErrorResponse
  setIsValidBic: Dispatch<SetStateAction<boolean>>
  type?: 'bank' | 'intermediaryBank'
}

type RoutingNumberValidationProps = {
  routingNumberBankDetails: ValidateRoutingNumberResponse
  isValidatingRoutingNumberError: boolean
  setIsRoutingNumberValid: Dispatch<SetStateAction<boolean>>
}

type ScanValidationProps = {
  scanBankDetails: ValidateScanResponse
  isValidatingScanError: boolean
  setIsValidScan: Dispatch<SetStateAction<boolean>>
}

type UseBankDetailValidationProps = {
  // IBAN
  ibanValidationProps?: IbanValidationProps
  // BIC
  bicValidationProps?: BicValidationProps
  // Routing number
  routingNumberValidationProps?: RoutingNumberValidationProps
  // Scan
  scanValidationProps?: ScanValidationProps
  // Form
  formValues: ContactFiatInstrumentFormData
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>
  setError: UseFormSetError<ContactFiatInstrumentFormData>
  clearErrors: UseFormClearErrors<ContactFiatInstrumentFormData>
  setRequestError?: Dispatch<SetStateAction<MappedErrorMessageState>>
  // Beneficiary
  contactBeneficiary?: BeneficiaryDetails
  // IBAN state
  isIbanFormat?: boolean
  isValidIban?: boolean
}

export const useBankDetailValidation = ({
  // IBAN
  ibanValidationProps,
  // BIC
  bicValidationProps,
  // Routing number
  routingNumberValidationProps,
  // Scan
  scanValidationProps,
  // Form values
  formValues,
  setValue,
  setError,
  clearErrors,
  // Beneficiary
  contactBeneficiary,
  // IBAN state
  isIbanFormat = false,
  isValidIban = false,
}: UseBankDetailValidationProps) => {
  const { t } = useTranslation()

  // IBAN validation
  useEffect(() => {
    if (!ibanValidationProps) return
    if (contactBeneficiary) return

    const { isValidatingIbanError, ibanBankDetails, setIsValidIban, setIbanBankDetailsData } =
      ibanValidationProps ?? {}

    if (!ibanBankDetails) return

    clearErrors()

    const isValidIban = getIsValidIban({
      paymentMethods: formValues?.paymentMethods as PaymentMethod[],
      currency: formValues?.currency,
      ibanBankDetails,
    })

    if (!isValidIban && !isValidatingIbanError && formValues?.accountNumber) {
      const countryDoesNotSupportIban = ibanBankDetails?.validationFailed?.includes(
        IBAN_VALIDATION_ERRORS.COUNTRY_DOES_NOT_SUPPORT_IBAN
      )

      if (!countryDoesNotSupportIban) {
        setIsValidIban(false)
        setValue('bankAddressLine1', '')
        setValue('bankAddressLine2', '')
        setValue('bankCity', '')
        setValue('bankRegion', '')
        setValue('bankPostCode', '')
        setValue('bankCode', '')
        setValue('bankName', '')
      }
      return
    }

    if (isValidIban) {
      setIsValidIban(true)
    }

    const details = setBankAccountFieldsFetchedFromIbanValidation(formValues, ibanBankDetails)
    setIbanBankDetailsData(details)
  }, [
    ibanValidationProps?.ibanBankDetails,
    ibanValidationProps?.isValidatingIbanError,
    formValues?.accountNumber,
    formValues?.currency,
    clearErrors,
    contactBeneficiary,
  ])

  // BIC validation
  useEffect(() => {
    if (!bicValidationProps) return
    if (contactBeneficiary) return // Skip validation if editing existing beneficiary

    const {
      isValidatingBicError,
      bicValidationData,
      bicValidationError,
      setIsValidBic,
      type = 'bank',
    } = bicValidationProps ?? {}

    const fieldName: keyof ContactFiatInstrumentFormData =
      type === 'intermediaryBank' ? 'intermediaryBankCode' : 'bankCode'

    if (type === 'bank' && formValues?.currency !== BeneficiaryDetailsCurrency.USD) return

    if (isValidatingBicError) {
      const errorMessage =
        bicValidationError?.data?.errorList?.[0]?.message ??
        bicValidationError?.errorList?.[0]?.message ??
        t('bicValidationError')
      setError(fieldName, { message: errorMessage })
      setIsValidBic(false)
      return
    }

    if (!bicValidationData) return

    const isValidBic = bicValidationData === 'Valid'
    const fieldValue = formValues?.[fieldName]

    if (!isValidBic && fieldValue) {
      setError(fieldName, { message: t('invalidBicCodeErrorMessage') })
      setIsValidBic(false)
    } else {
      clearErrors()
      setIsValidBic(true)
    }
  }, [
    bicValidationProps?.bicValidationData,
    bicValidationProps?.isValidatingBicError,
    bicValidationProps?.bicValidationError,
    bicValidationProps?.type,
    formValues?.currency,
    contactBeneficiary,
  ])

  // USD validation
  useEffect(() => {
    if (!routingNumberValidationProps) return
    if (contactBeneficiary) return

    const { isValidatingRoutingNumberError, routingNumberBankDetails, setIsRoutingNumberValid } =
      routingNumberValidationProps ?? {}

    if (formValues?.currency !== BeneficiaryDetailsCurrency.USD) return
    if (!routingNumberBankDetails) return

    const { bankDetails, validationFailed } = routingNumberBankDetails
    const { bankAddress, bankName } = bankDetails ?? {}

    if (isValidatingRoutingNumberError) {
      setError('routingNumber', { message: t('routingNumberValidationError') })
      setIsRoutingNumberValid(false)
      return
    }

    const isValid = !!bankAddress && !validationFailed?.length

    setIsRoutingNumberValid(isValid)

    if (!isValid) {
      formValues?.routingNumber &&
        setError('routingNumber', { message: t('abaRoutingNumberError') })
      if (!isIbanFormat || !isValidIban) {
        resetBankAddress(setValue, 'bank')
        setValue('bankName', '')
      }
    } else {
      clearErrors()
      if (!isIbanFormat || !isValidIban) {
        const { addressLine1, addressLine2, city, region, postCode } = bankAddress || {}
        setValue('bankName', bankName)
        setValue('bankAddressLine1', addressLine1)
        setValue('bankAddressLine2', addressLine2)
        setValue('bankCity', city)
        setValue('bankRegion', region)
        setValue('beneficiaryPostCode', postCode)
      }
    }
  }, [
    routingNumberValidationProps?.routingNumberBankDetails,
    routingNumberValidationProps?.isValidatingRoutingNumberError,
    formValues?.currency,
    contactBeneficiary,
    isIbanFormat,
    isValidIban,
  ])

  // Scan validation
  useEffect(() => {
    if (!scanValidationProps) return
    if (contactBeneficiary) return // Skip validation if editing existing beneficiary

    if (formValues?.currency !== BeneficiaryDetailsCurrency.GBP) return

    const { scanBankDetails, isValidatingScanError, setIsValidScan } = scanValidationProps ?? {}
    const { supportedPaymentMethods } = scanBankDetails ?? {}

    if (!scanBankDetails) return

    // GBP validation
    const isFPSandChapsSupported =
      supportedPaymentMethods?.includes('CHAPS') ||
      supportedPaymentMethods?.includes('BACS') ||
      supportedPaymentMethods?.includes('FASTER_PAYMENT') ||
      supportedPaymentMethods?.includes('CCC_PAYMENTS')

    if (isValidatingScanError) {
      setError('accountNumber', { message: t('scanValidationError') })
      setError('bankCode', { message: t('scanValidationError') })
      setIsValidScan(false)
      return
    }

    if (scanBankDetails && !isFPSandChapsSupported) {
      setError('accountNumber', { message: t('reachabilityCheckErrorMessage') })
      setError('bankCode', { message: t('reachabilityCheckErrorMessage') })
      setIsValidScan(false)
      return
    }

    setIsValidScan(true)
  }, [
    scanValidationProps?.scanBankDetails,
    scanValidationProps?.isValidatingScanError,
    formValues?.currency,
    contactBeneficiary,
  ])
}
